import React, { forwardRef, useRef, useState, useImperativeHandle } from "react";
import { RCCButton, RCCGrid, RCCHookToast, RCCRangeSlider, RCCRefModalProps, RCCServicesApi, RCCTextArea } from "react-common-components";
import { useForm } from "react-hook-form";
import DateTimePicker from "src/components/DateTimePicker";
import InputNumber from "src/components/InputNumber";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import Attachments from "src/components/Attachments";
import Tabs from "src/components/Tabs";
import TimelineVertical from "src/components/TimelineVertical";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import removeDifferenceBetweenUTCAndLocal from "src/utils/removeDifferenceBetweenUTCAndLocal";
import stringToNumber from "src/utils/stringToNumber";
import { ActionType, ICheckListAction, Intbb004, IRefActionsProgressModal } from "../interface";
import { changeProgressSchema } from "../schemas";

const ModalProgress: React.ForwardRefRenderFunction<IRefActionsProgressModal> = (_, ref) => {
	const { translate, currentLanguage } = useTranslation();
	const { addToast } = RCCHookToast();
	const { setLoading } = useLoaderBackdrop();
    
	// Modal comments
	const modalProgress = useRef<RCCRefModalProps>(null);
	const onCallbackFunctionClose = useRef<Function | undefined>(undefined);
	const onCallbackFunction = useRef<Function | undefined>(undefined);

	const [actionId, setActionId] = useState(0);
	const [isTask, setIsTask] = useState(false);
	const [checklists, setChecklists] = useState<ICheckListAction[]>([]);
	const [lastHistoric, setLastHistoric] = useState<Intbb004>();
	const [subtitleModal, setSubtitleModal] = useState<string>("");
	const [defaultPercentage, setDefaultPercentage] = useState<number>(0);
	const [isReopen, setIsReopen] = useState(false);
	const [finishing, setFinishing] = useState(false);
	const [alreadyStarted, setAlreadyStarted] = useState(false);
	const [hasSubactionsOpened, setHasSubactionsOpened] = useState(false);
	const [disabledForPausedAction, setDisabledForPausedAction] = useState(false);

	
	const { 
		handleSubmit : handleSubmitChangeProgress, 
		register : registerChangeProgress, 
		control: controlChangeProgress,
		reset : resetChangeProgress, 
		setError : setErrorChangeProgress, 
		formState: { errors : errorsChangeProgress }, 
		clearErrors: clearErrorsChangeProgress, 
		setValue: setValueChangeProgress
	} = useForm();

	const openModal = async (id: number, subtitle: string, currentPercentage: number,  onCallback?: Function, reopen?: boolean, onCallbackClose?: Function, isFinishing?: boolean) => {
		resetChangeProgress();
		try{
			setLoading(true);
			const {data} = await RCCServicesApi.getRequest({
				method: "Actions/SelectActionForChangeProgress",
				urlParameters: {
					id
				}
			});

			setIsTask(data.inb001Tipo === ActionType.TASK);
			setChecklists(reopen ? data.intbb009.map((inb009: ICheckListAction) => ({
				...inb009,
				checked: false
			})) : data.intbb009);

			if(reopen)
				setDefaultPercentage(0);
			else if(data.intbb009 && data.intbb009.length > 0 && data.intbb009?.every((e: any) => e.checked))
				setDefaultPercentage(100);
			else
				setDefaultPercentage(currentPercentage);	

			onCallbackFunctionClose.current = onCallbackClose;
			onCallbackFunction.current = onCallback;
			if(data.inb001Tipo === ActionType.TASK && data.alreadyStarted && !reopen && !isFinishing)
				setDisabledForPausedAction(true);
			else
				setDisabledForPausedAction(false);
			setLastHistoric(data.intbb004?.inb004Id ? data.intbb004 : undefined);
			setHasSubactionsOpened(data.hasSubactionsOpened);
			setActionId(id);
			setSubtitleModal(subtitle);
			
			setFinishing(isFinishing || false);
			setLoading(false);
			setAlreadyStarted(data.alreadyStarted);
			setIsReopen(reopen || false);
			modalProgress.current?.openModal();
		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao selecionar ação"), addToast);
		}
	};

	const submit = async(data: any) => {
		const dataForm = data;

		try{
			clearErrorsChangeProgress();
			if((alreadyStarted && !isReopen && !finishing) &&isTask && data.dateEnd !== null && data.dateEnd < data.date){
				addToast({
					title: translate("Erro"),
					description: translate("Data Final tem que ser após a Data Inicial"),
					color: "yellow"
				});
				return;
			}
			if((alreadyStarted && !isReopen && !finishing) && isTask && (data.dateEnd === undefined || data.dateEnd === null)){
				addToast({
					title: translate("Erro"),
					description: translate("Essa ação é uma tarefa, portanto insira uma data final para esse andamento"),
					color: "yellow"
				});
				return;
			}

			if(hasSubactionsOpened && dataForm.percConclusao >= 100){
				addToast({
					title: translate("Erro ao concluir ação"),
					description: translate("Ação tem subação em aberto"),
					color: "yellow"
				});
				return;
			}

		
			if(isTask && alreadyStarted && !isReopen && !finishing){
				// ajuste para fechar a hora corretamente
				const secondsDateStart = dataForm.date.getSeconds() + 1;
				dataForm.dateEnd.setSeconds(secondsDateStart);
			}

			if(isTask && !isReopen && !finishing){
				if(!alreadyStarted)
					dataForm.dateEnd = null;
				if(!(checklists.length > 0))
					dataForm.percConclusao = dataForm.percConclusao === 100 ? 100 : 1;
			}

			if(checklists.length > 0){
				if(checklists.every((e) => e.checked) && !isReopen && !finishing){
					dataForm.percConclusao = 100;
				}else{
					const per = Math.trunc(defaultPercentage);
					dataForm.percConclusao = per < 0 ? 0 : per;
				}
			}

			modalProgress.current?.setLoadingButton();
			await changeProgressSchema(translate).validate({...data }, {
				abortEarly: false
			});

			// TESTAR EM PROD
			// Retira a diferença de local e UTC
			// O método JSON.stringify passa as datas para o formato UTC automaticamente 
			dataForm.date = removeDifferenceBetweenUTCAndLocal(dataForm.date);
			if(dataForm.dateEnd)
				dataForm.dateEnd = removeDifferenceBetweenUTCAndLocal(dataForm.dateEnd);

			dataForm.value = typeof data.value === "string" ? stringToNumber(data.value, currentLanguage) : data.value;
			dataForm.valueRevenue = typeof data.valueRevenue === "string" ? stringToNumber(data.valueRevenue, currentLanguage) : data.valueRevenue;

			const formData = new FormData();
			formData.append("Obj", JSON.stringify({
				...dataForm,
				actionId
			}));

			data.attachments.forEach((attach: any, index: number) => {
				formData.append(`Attachments[${index}]`, attach);
			});

			await RCCServicesApi.postRequest({
				method: "Actions/InsertChangeProgress",
				contentType: "multipart/form-data",
				body: formData
			});

			addToast({
				color: "green",
				description: translate("Andamento alterado com sucesso"),
				title: translate("Sucesso")
			});
				
			modalProgress.current?.resetLoadingButton();
			modalProgress.current?.closeModal();
				
			if(onCallbackFunction.current)
				onCallbackFunction.current();

		}catch(err){
			modalProgress.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorChangeProgress)){
				addToast({
					title: translate("Erro"),
					description: translate("Verifique os erros no formulário e tente novamente"),
					color: "yellow"
				});
				return;
			}

			standardErrorMessages(err, translate("Erro ao alterar andamento"), addToast);
		}
	};
	
	const onCallbackSelectCheckbox = (data: ICheckListAction, checked: boolean) => {
		const percConclusao = defaultPercentage >= 0 ? defaultPercentage : 0;

		setChecklists((prevState) => prevState.map((e) => {
			if(e.id === data.id)
				return {...e, checked};
			return e;
		}));

		const increment = ((data.weight * 100 / data.sumWeight) * data.weightChecklist) / data.sumWeightChecklist;
		let newPercentage;
		if(checked)
			newPercentage = percConclusao + increment;
		else
			newPercentage = percConclusao - increment;

		setDefaultPercentage(newPercentage);		
	};

	useImperativeHandle(ref, () => ({
		openModalChangeProgress: openModal
	}));

	const titleModal = () => {
		if(isReopen)
			return translate("Reabrir ação");
		if(finishing)
			return translate("Finalizar ação");
		return translate("Alterar andamento");
	};

	const buttonSaveModal = () => {
		if(isTask && !alreadyStarted && !isReopen && !finishing)
			return translate("Iniciar");
		if(isTask && alreadyStarted && lastHistoric?.inb004Datafinal && !isReopen && !finishing)
			return translate("Iniciar");
		if(isTask && alreadyStarted && !isReopen && !finishing)
			return translate("Pausar");
		return translate("Salvar");
	};

	const valueOrDefault = (value: number | undefined | null) => value && value > 0 ? value : 0;

	const returnValue = (checklistsX: ICheckListAction[], defaultPercentageX: number) => {
		if(checklistsX && checklistsX.length > 0 && checklistsX.every((e) => e.checked))
			return 100;
		if(checklistsX && checklistsX.length > 0)
			return  Math.trunc(defaultPercentageX);
		return undefined;
	};

	return (
		<Modal
			ref={modalProgress}
			header={{
				title: titleModal(),
				subtitle: `${translate("Ação")}: ${subtitleModal}`,
				icon: {
					name: "check"
				}
			}}
			footer= {{
				customModalFooter:
				<>
					<RCCButton 
						size="md" 
						type="button" 
						color="green"
						onClick={handleSubmitChangeProgress(submit)}
					> 
						{buttonSaveModal()} 
					</RCCButton>   
					{isTask && alreadyStarted && !isReopen && !finishing && (
						<RCCButton 
							size="md" 
							type="button" 
							color="red"
							onClick={handleSubmitChangeProgress((data: any) => {
								submit({
									...data,
									percConclusao: 100
								});
							})}
						> 
							{translate("Finalizar")} 
						</RCCButton>
					)} 
					<RCCButton 
						size="md" 
						type="button" 
						onClick={() => {
							modalProgress.current?.closeModal();
						}}
					> 
						{translate("Cancelar")} 
					</RCCButton>
				</>
			}}
			styleInline={ModalSide}
			onClose={() => {if(onCallbackFunctionClose.current) onCallbackFunctionClose.current();}}
		>
			<Tabs 
				name="TabActionsChangeProgress"
				tabInit={0}
				options={[{
					label: translate("Geral"),
					content: (
						<>
							<DateTimePicker 
								label={translate("Data")}
								name="date"
								type={isTask ? "dateTimePicker" : "datePicker"}
								required
								error={errorsChangeProgress.date}
								defaultValue={isTask && alreadyStarted && !isReopen && !finishing ? new Date(lastHistoric?.inb004Data ?? "") : new Date()}	
								control={controlChangeProgress}
							/>
							{isTask && !isReopen && !finishing && alreadyStarted  && (
								<DateTimePicker 
									label={translate("Data final")}
									name="dateEnd"
									type={isTask ? "dateTimePicker" : "datePicker"}
									defaultValue={isTask && alreadyStarted && !isReopen && !finishing ? new Date() : undefined}	
									error={errorsChangeProgress.dateEnd}
									control={controlChangeProgress}
								/>
							)}
							<RCCGrid container item>
								<RCCTextArea 
									name="description"
									label={translate("Descrição")}
									maxLength={4000}
									required
									error={errorsChangeProgress.description}
									defaultValue={isTask && alreadyStarted && !isReopen && !finishing ? lastHistoric?.inb004Descricao : ""}	
									register={registerChangeProgress}
								/>
							</RCCGrid>

							<div
								style={isReopen ? { display: "none" } : {}}
							>
								{(isTask && alreadyStarted && !isReopen && !finishing && !disabledForPausedAction) || (isTask && checklists.length > 0) || (isTask && finishing) || (isTask && isReopen) || !isTask ? (
									<RCCRangeSlider
										control={controlChangeProgress}
										name="percConclusao"
										color="purple"
										label={translate("Conclusão")}
										defaultValue={defaultPercentage}
										value={returnValue(checklists,defaultPercentage)}
										min={0}
										max={100}
										disabled={finishing || isReopen || checklists.length > 0}
										valueLabelDisplay="on"
										valueLabelFormat={(x: number) => `${Math.floor(x)}%`}
										styleInline=".MuiSlider-valueLabel > span > span{ color: var(--input-font-color); }"
									/>
								): null}
							
							
								<RCCGrid container item spacing={1}>
									<RCCGrid container item xs={12} sm={6}>
										<InputNumber 
											label={translate("Valor realizado despesa")}
											name="value"
											defaultValue={isTask && alreadyStarted && !isReopen && !finishing ? valueOrDefault(lastHistoric?.inb004Vlrrealdespesa) : 0}	
											control={controlChangeProgress}
											error={errorsChangeProgress.value}
											required
										/>
									</RCCGrid>
									<RCCGrid container item xs={12} sm={6}>
										<InputNumber 
											label={translate("Valor realizado receita")}
											name="valueRevenue"
											defaultValue={isTask && alreadyStarted && !isReopen && !finishing ? valueOrDefault(lastHistoric?.inb004Vlrrealreceita) : 0}	
											control={controlChangeProgress}
											error={errorsChangeProgress.valueRevenue}
											required
										/>
									</RCCGrid>
								</RCCGrid>
							
								{checklists.length > 0 && (
									<TimelineVertical 
										data={checklists} 
										nameOfCheckbox="inb010Verificado"
										nameOfID="inb010Id"
										nameOfObject="intbb010"
										register={registerChangeProgress}
										onCallbackSelectCheckbox={onCallbackSelectCheckbox}
										submitPreviouslyCheckedValues
										restrictSequentialCheckin={false}
										disabled={isReopen}
									/>
								)}
							</div>
							
						</>
					)
				}, {
					label: translate("Anexos"),
					content: (
						<>
							<Attachments 
								name="attachments" 
								setValue={setValueChangeProgress}
							/>

						</>
					)
				}]}
			/>
		</Modal>
	);
};

export default forwardRef(ModalProgress);
